import { useEffect, useRef, useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { fetchRepositories } from '../services/RepositoryService';
import { fetchRepositoriesSuccess } from '../store/slices/repositorySlice';
import { Repository } from '../types/Repository.types';

interface UseRepositoryPollingOptions {
  systemId: string | undefined;
  pollingInterval?: number;
  onSyncComplete?: (repository: Repository) => void;
}

// A repository sync + analysis job can legitimately run long on a slow
// self-hosted AI backend, but a permanently stuck job (crashed worker,
// orphaned queue message) must not poll silently forever. 1200 polls x 3s
// = 1 hour before this hook gives up and stops on its own; callers can read
// `isStalled` to show a "still going / give up" affordance if they want one.
const MAX_POLL_COUNT = 1200;

/**
 * Smart polling hook that only polls when repositories have syncInProgress=true
 *
 * Features:
 * - Auto-starts polling when any repository is syncing
 * - Auto-stops when all syncs complete
 * - Notifies via callback when sync completes
 * - Prevents multiple intervals from running
 */
export function useRepositoryPolling({
  systemId,
  pollingInterval = 3000, // 3 seconds
  onSyncComplete
}: UseRepositoryPollingOptions) {
  const dispatch = useAppDispatch();
  const repositories = useAppSelector((state) => state.repository.repositories);
  const isOffline = useAppSelector((state) => state.appStatus.isOffline);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousSyncingRef = useRef<Set<string>>(new Set());
  const pollCountRef = useRef<number>(0);
  const [isStalled, setIsStalled] = useState(false);

  // Check if any repository is syncing
  const hasSyncingRepositories = repositories.some(
    (repo) => repo.syncInProgress
  );

  // Debug log
  useEffect(() => {
    const syncingRepos = repositories.filter((r) => r.syncInProgress);
    if (syncingRepos.length > 0) {
      console.log(
        '🔄 Syncing repositories detected:',
        syncingRepos.map((r) => ({ id: r.id, name: r.name }))
      );
    }
  }, [repositories]);

  const pollRepositories = useCallback(async () => {
    if (!systemId || isOffline) return;

    pollCountRef.current += 1;
    console.log(
      `📡 Poll #${pollCountRef.current} at ${new Date().toLocaleTimeString()}`
    );

    if (pollCountRef.current > MAX_POLL_COUNT) {
      console.warn(
        `⏱️ Repository polling exceeded ${MAX_POLL_COUNT} attempts — stopping automatically`
      );
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsStalled(true);
      return;
    }

    try {
      const response = await fetchRepositories({ aiSystemId: systemId });
      dispatch(fetchRepositoriesSuccess(response));

      // Check for newly completed syncs
      if (onSyncComplete) {
        const currentSyncing = new Set(
          response.data
            .filter((repo) => repo.syncInProgress)
            .map((repo) => repo.id)
        );

        // Find repositories that were syncing but are not anymore
        previousSyncingRef.current.forEach((repoId) => {
          if (!currentSyncing.has(repoId)) {
            const completedRepo = response.data.find(
              (repo) => repo.id === repoId
            );
            if (completedRepo) {
              onSyncComplete(completedRepo);
            }
          }
        });

        previousSyncingRef.current = currentSyncing as Set<string>;
      }
    } catch (error) {
      console.error('Polling failed:', error);
      // Don't stop polling on error, just log it
    }
  }, [systemId, isOffline, dispatch, onSyncComplete]);

  useEffect(() => {
    if (isOffline && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    if (isOffline) {
      return;
    }

    // Start polling when repositories are syncing
    if (hasSyncingRepositories && !intervalRef.current) {
      console.log(
        `🔄 Starting repository polling (interval: ${pollingInterval}ms)`
      );
      pollCountRef.current = 0;
      setIsStalled(false);

      // Poll immediately (don't wait for first interval)
      pollRepositories();

      // Then poll at interval
      const interval = setInterval(() => {
        pollRepositories();
      }, pollingInterval);

      intervalRef.current = interval;
    }

    // Stop polling when no repositories are syncing
    if (!hasSyncingRepositories && intervalRef.current) {
      console.log(
        `✅ Stopping repository polling (completed ${pollCountRef.current} polls)`
      );
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      previousSyncingRef.current.clear();
      pollCountRef.current = 0;
    }
  }, [hasSyncingRepositories, isOffline, pollRepositories, pollingInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        console.log('🧹 Cleaning up polling interval on unmount');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return {
    isPolling: !!intervalRef.current,
    hasSyncingRepositories,
    isStalled
  };
}
